import { useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { cn } from "../../../../lib/utils";
import { useClaimMotion } from "./claimMotion";

/**
 * The section switcher for narrow screens.
 *
 * Rebuilt as a real tablist. The old one was four plain buttons: nothing
 * announced them as tabs, nothing tied a button to the panel it controlled, and
 * arrow keys did nothing — so on a tablet with a keyboard the only way through
 * was Tab, Tab, Tab, Enter.
 *
 * The underline is a single shared element moved by `layoutId` rather than a
 * border toggled per button, so it slides between tabs instead of blinking from
 * one to the next.
 */
const ClaimSectionTabs = ({ tabs, activeKey, onChange, idPrefix }) => {
  const { t, i18n } = useTranslation();
  const { reduceMotion } = useClaimMotion();
  const rtl = i18n.resolvedLanguage === "ar";
  const buttonRefs = useRef({});

  const focusTab = (key) => {
    onChange(key);
    // Keep focus with the selection so the next arrow press continues from
    // where the user is, not from where they started.
    window.requestAnimationFrame(() => buttonRefs.current[key]?.focus());
  };

  const onKeyDown = (event) => {
    const keys = tabs.map((tab) => tab.key);
    const current = keys.indexOf(activeKey);
    // Arrow keys follow what the user sees, so they swap in Arabic.
    const forward = rtl ? "ArrowLeft" : "ArrowRight";
    const backward = rtl ? "ArrowRight" : "ArrowLeft";

    if (event.key === forward) {
      event.preventDefault();
      focusTab(keys[(current + 1) % keys.length]);
    } else if (event.key === backward) {
      event.preventDefault();
      focusTab(keys[(current - 1 + keys.length) % keys.length]);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusTab(keys[0]);
    } else if (event.key === "End") {
      event.preventDefault();
      focusTab(keys[keys.length - 1]);
    }
  };

  return (
    <div
      role="tablist"
      aria-label={t("motorInsurance.trackClaimFlow.breadcrumb.claim_details")}
      onKeyDown={onKeyDown}
      // Four labels do not fit across a 320px phone. Scrolling beats wrapping
      // to a second row, which would push the content below the fold.
      // overflow-x: auto turns the other axis to auto as well, so the vertical
      // axis is clipped explicitly: the row used to scroll up and down by the
      // pixel the underline hung below it. The divider is an inset shadow
      // rather than a border so the underline can sit inside the row and
      // still cover it.
      className="-mx-1 flex gap-1 overflow-x-auto overflow-y-hidden px-1 shadow-[inset_0_-1px_0_#DDE2E4] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {tabs.map((tab) => {
        const selected = tab.key === activeKey;
        return (
          <button
            key={tab.key}
            ref={(node) => {
              buttonRefs.current[tab.key] = node;
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${tab.key}`}
            aria-controls={`${idPrefix}-panel-${tab.key}`}
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.key)}
            className={cn(
              "relative flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-3 text-sm font-semibold transition-colors duration-150",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#00A9E7]",
              selected
                ? "text-[#00668F]"
                : "text-[#6E7C87] hover:text-[#1A2024]"
            )}
          >
            {tab.label}

            {tab.count > 0 && (
              <span
                className={cn(
                  "inline-flex min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] font-semibold leading-[18px]",
                  selected
                    ? "bg-[#E4F5FD] text-[#00668F]"
                    : "bg-[#EDF1F3] text-[#5B6871]"
                )}
              >
                {tab.count}
              </span>
            )}

            {selected && (
              <motion.span
                layoutId={`${idPrefix}-tab-underline`}
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-[#00A9E7]"
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : { type: "spring", stiffness: 420, damping: 34 }
                }
              />
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ClaimSectionTabs;
